import React from 'react';
import { ChevronRight, IndianRupee, Pencil, Percent, Plus, Sigma, Trash2 } from 'lucide-react';

const actionIconBtnClass =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#475569] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CBD5E1] focus-visible:ring-offset-1';

const initialsThemes = [
  'bg-[#E7F2EF] border-[#BFE6DE] text-[#2A565E]',
  'bg-[#EEF2FF] border-[#D6DBFF] text-[#4F46E5]',
  'bg-[#FFF4E8] border-[#FBD7B2] text-[#B45309]',
  'bg-[#F3E8FF] border-[#E2CCFF] text-[#7E22CE]',
  'bg-[#EAF6FF] border-[#C5E4FA] text-[#0369A1]',
  'bg-[#F0FDF4] border-[#BBF7D0] text-[#166534]',
  'bg-[#FDF4FF] border-[#E9D5FF] text-[#86198F]'
];

const VISIBLE_ACCOMMODATIONS = 4;

const formatInr = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

const ExtraChargeCard = ({ charge, totals, onEdit, onDelete, onViewTotal }) => {
  const initials = (charge?.name || 'EC')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const avatarSeed = String(charge?.id ?? charge?.name ?? 'EC')
    .split('')
    .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const initialsTheme = initialsThemes[avatarSeed % initialsThemes.length];

  const accNames = (charge?.accomodations || [])
    .map((a) => a?.accomodation?.name)
    .filter(Boolean);

  const isRequired = charge?.isMandatory === true || charge?.isMandatory === 'true';

  const priceNum = Number(charge?.price) || 0;
  const gstPct = charge?.gst != null && charge?.gst !== '' ? Number(charge.gst) : null;
  const gstAmount =
    gstPct != null && !Number.isNaN(gstPct) ? (priceNum * gstPct) / 100 : null;

  const modeUnit = charge?.mode === 'sqft' ? 'sq/ft' : charge?.mode === 'sqmt' ? 'sq/mt' : null;
  const modeLabel =
    charge?.mode === 'accommodation' ? 'Flat' : modeUnit ? `Per ${modeUnit}` : charge?.mode || '—';

  return (
    <article className="group min-h-[200px] bg-white rounded-2xl border border-[#E2E8F0] p-4 flex flex-col gap-2.5 shadow-sm relative transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        <button
          type="button"
          aria-label="Edit extra charge"
          title="Edit"
          className={actionIconBtnClass}
          onClick={() => onEdit?.(charge.id)}>
          <Pencil size={17} strokeWidth={1.75} className="text-current" />
        </button>
        <button
          type="button"
          aria-label="Delete extra charge"
          title="Delete"
          className={actionIconBtnClass}
          onClick={() => onDelete?.(charge.id)}>
          <Trash2 size={17} strokeWidth={1.75} className="text-current" />
        </button>
      </div>

      <div className="flex-1 flex flex-col gap-2.5 pr-20">
        <div className="flex items-start gap-3">
          <div
            className={`h-14 w-14 rounded-xl border flex items-center justify-center text-lg font-semibold shrink-0 ${initialsTheme}`}>
            {initials || 'EC'}
          </div>
          <div className="min-w-0">
            <p
              className="text-[18px] leading-tight font-semibold text-[#1A202C] truncate"
              title={charge?.name || ''}>
              {charge?.name || '---'}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold bg-[#F3E8FF] text-[#6B46C1]">
                {modeLabel}
              </span>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold ${
                  isRequired
                    ? 'bg-[#FEE2E2] text-[#B91C1C]'
                    : 'bg-[#F1F5F9] text-[#64748B]'
                }`}>
                {isRequired ? 'Required' : 'Optional'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {accNames.length > 0 ? (
            <>
              {accNames.slice(0, VISIBLE_ACCOMMODATIONS).map(
                (name, idx) => (
                  <span
                    key={`${charge.id}-acc-${idx}`}
                    className="inline-flex max-w-full items-center rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-2.5 py-1 text-[12px] font-medium text-[#475569] truncate"
                    title={name}>
                    {name}
                  </span>
                )
              )}
              {accNames.length > VISIBLE_ACCOMMODATIONS && (
                <button
                  type="button"
                  onClick={() => onViewTotal?.(charge)}
                  title={`View all accommodations: ${accNames.slice(VISIBLE_ACCOMMODATIONS).join(', ')}`}
                  className="inline-flex items-center gap-0.5 rounded-full border border-[#BFD4D8] bg-[#E8F3F5] px-2.5 py-1 text-[12px] font-semibold text-[#2A565E] hover:bg-[#D6EAEE] transition-colors">
                  <Plus size={12} strokeWidth={2.5} />
                  {accNames.length - VISIBLE_ACCOMMODATIONS}
                </button>
              )}
            </>
          ) : (
            <span className="inline-flex rounded-full border border-dashed border-[#CBD5E1] bg-white px-2.5 py-1 text-[12px] text-[#94A3B8]">
              No accommodations
            </span>
          )}
        </div>

        {charge?.description ? (
          <p className="text-xs text-[#94A3B8] line-clamp-2" title={charge.description}>
            {charge.description}
          </p>
        ) : null}

        <div className="mt-auto flex flex-col gap-2.5 border-t border-[#EEF2F6] pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#E6FFFA] text-[#2C7A7B]">
              <IndianRupee size={12} strokeWidth={2.5} />
              {formatInr(priceNum)}
              {modeUnit && <span className="font-medium opacity-80">/ {modeUnit}</span>}
            </span>
            {gstPct != null && !Number.isNaN(gstPct) && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EBF4FF] text-[#5A67D8]">
                <Percent size={12} strokeWidth={2.5} />
                GST {gstPct.toFixed(2)}%
                {gstAmount != null && (
                  <span className="font-medium opacity-90"> (₹{formatInr(gstAmount)})</span>
                )}
              </span>
            )}
          </div>
          {totals && (
            <button
              type="button"
              onClick={() => onViewTotal?.(charge)}
              title="View full breakdown"
              className="group/total flex w-full items-center justify-between gap-3 rounded-xl border border-[#FBD7B2] bg-[#FFF8F0] px-3 py-2 text-left transition-colors hover:bg-[#FFEFDC]">
              <span className="flex items-center gap-2 min-w-0">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#FFE8CC] text-[#B45309]">
                  <Sigma size={14} strokeWidth={2.5} />
                </span>
                <span className="flex flex-col leading-tight min-w-0">
                  <span className="text-[11px] font-medium text-[#B45309]">
                    Total · {totals.unitCount} {totals.unitCount === 1 ? 'unit' : 'units'}
                  </span>
                  <span className="text-[15px] font-semibold text-[#1A202C] truncate">
                    ₹{formatInr(totals.total)}
                  </span>
                </span>
              </span>
              <span className="flex shrink-0 items-center text-[12px] font-medium text-[#B45309]">
                Breakdown
                <ChevronRight size={14} className="transition-transform group-hover/total:translate-x-0.5" />
              </span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default ExtraChargeCard;
